/**
 * useAndroidBackButton — makes Android's back gesture/button behave.
 *
 * iOS has no hardware back, so the app shipped without any handling: on
 * Android that means the very first back press KILLS the app from any
 * screen, mid-draft included. Capacitor only suppresses that default once
 * something subscribes to `backButton`, which is what this hook does.
 *
 * Order of precedence on a back press:
 *   1. An open overlay (drawer, sheet, modal) gets first refusal. Any
 *      component can claim the press by listening for the cancelable
 *      `de:androidback` event and calling preventDefault() before closing
 *      itself — see useAndroidBackDismiss.
 *   2. Otherwise pop history, matching the iOS swipe-back rule exactly
 *      (SwipeBackShell.performBack): go back when there is somewhere to go.
 *   3. At the history root, land on the home tab rather than exiting, so
 *      back never dumps a user out of a deep screen.
 *   4. Only from home, with nothing to pop, does a press arm exit, and it
 *      takes a SECOND press inside the window to actually leave. A single
 *      stray press closing a draft app is the complaint this prevents.
 *
 * Web and iOS mount this as a no-op.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { nativePlatform, type CapacitorListenerHandle } from '@/lib/platform';

/** Route the app treats as home for back purposes. */
const HOME_ROUTE = '/';

/** How long a first "press again to exit" stays armed. */
const EXIT_WINDOW_MS = 2000;

/** Cancelable event overlays listen for to claim the back press. */
export const ANDROID_BACK_EVENT = 'de:androidback';

export function useAndroidBackButton(): void {
  const navigate = useNavigate();
  const location = useLocation();
  // Refs, not state: re-rendering on an arm/disarm would be pointless churn.
  const exitArmedAt = useRef<number>(0);

  // The live handler. Kept in a ref so the SUBSCRIBE effect can run exactly
  // once: react-router's `navigate` identity changes with router state, so
  // depending on it tore the native listener down and re-added it on every
  // tab change (a stream of "Removing listener App backButton" in logcat).
  // That churn leaves a window with no listener, during which a back press
  // falls through to Android's default — exit the app.
  const handlerRef = useRef<() => void>(() => {});

  handlerRef.current = () => {
    // 1. Give any open overlay the chance to absorb the press.
    const claim = new CustomEvent(ANDROID_BACK_EVENT, { cancelable: true });
    if (!window.dispatchEvent(claim)) {
      exitArmedAt.current = 0;
      return;
    }

    // 2. Pop history when there is history to pop.
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) {
      exitArmedAt.current = 0;
      navigate(-1);
      return;
    }

    // 3. Not home yet? Go home instead of leaving the app. Uses the ROUTER's
    // path: the native shell loads the bundle as https://localhost/index.html,
    // so comparing window.location.pathname to '/' never matched on device
    // and the exit branch below was unreachable.
    if (location.pathname !== HOME_ROUTE) {
      exitArmedAt.current = 0;
      navigate(HOME_ROUTE, { replace: true });
      return;
    }

    // 4. Home with nothing to pop: confirm before exiting.
    const now = Date.now();
    if (now - exitArmedAt.current < EXIT_WINDOW_MS) {
      void window.Capacitor?.Plugins?.App?.exitApp?.();
      return;
    }
    exitArmedAt.current = now;
    window.dispatchEvent(new CustomEvent('de:androidbackhint'));
  };

  useEffect(() => {
    if (nativePlatform() !== 'android') return undefined;
    const appPlugin = window.Capacitor?.Plugins?.App;
    if (!appPlugin?.addListener) return undefined;

    let removeListener: (() => void) | null = null;
    let cancelled = false;

    // Always call through the ref so this subscription never goes stale.
    const onBack = () => handlerRef.current();

    // The runtime bridge hands back the handle synchronously; the npm
    // wrapper hands back a Promise. Accept either — assuming a Promise
    // threw at boot and left the WebView blank.
    try {
      const result = appPlugin.addListener('backButton', onBack);
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        void (result as Promise<CapacitorListenerHandle>)
          .then((handle) => {
            // Unmounted before it resolved: drop it immediately so we never
            // leak a listener that outlives the component.
            if (cancelled) void handle.remove();
            else removeListener = () => handle.remove();
          })
          .catch(() => {
            /* bridge unavailable: default Android behavior stands */
          });
      } else {
        const handle = result as CapacitorListenerHandle;
        if (cancelled) void handle.remove();
        else removeListener = () => handle.remove();
      }
    } catch {
      /* bridge unavailable: default Android behavior stands */
    }

    return () => {
      cancelled = true;
      if (removeListener) void removeListener();
    };
    // Intentionally empty: subscribe once for the app's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
